import { Component, computed, inject, input, signal } from '@angular/core';
import { FieldTree, FormField, SchemaPathTree } from '@angular/forms/signals';
import { EarthingSettingsDto, ExternalCPCDto, MceApiService } from '../../../core/mceapi.client.generated';
import { Option } from '../../../shared/functions/functional';
import { apiOpt } from '../../../core/mceapi.client.helpers';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { rxResource } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';
import { SelectedCable } from '../selection/cable-selection.model';
import { createSystemVoltageDto, SystemVoltage } from '../system-voltage/system-voltage.model';


export interface EarthingSettings {
  screenBonded: boolean,
  armourBonded: boolean,
  useSpareCoreForEF: boolean,
}

export function buildEarthingSettingsSchema(path: SchemaPathTree<EarthingSettings>) { }

export function createEarthingSettings(settings: EarthingSettingsDto) {
  if (settings.screenBonded === undefined
    || settings.armourBonded === undefined
    || settings.useSpareCoreForEF === undefined) {
    throw new Error('Earthing settings DTO is missing required values.');
  }

  return signal<EarthingSettings>({
    screenBonded: settings.screenBonded,
    armourBonded: settings.armourBonded,
    useSpareCoreForEF: settings.useSpareCoreForEF
  });
}

export function createEarthingSettingsDto(model: FieldTree<EarthingSettings>, externalCPC:Option<ExternalCPCDto>) {
  return signal<EarthingSettingsDto>({
    screenBonded: model.screenBonded().value(),
    armourBonded: model.armourBonded().value(),
    useSpareCoreForEF: model.useSpareCoreForEF().value(),
    externalCPC: apiOpt(externalCPC)
  });
}

@Component({
  selector: 'app-earthing-settings',
  imports: [FormField, MatCheckboxModule],
  templateUrl: './earthing-settings.component.html',
  styles: ``,
})
export class EarthingSettingsComponent {

  readonly form = input.required<FieldTree<EarthingSettings>>();
  readonly selectedCable = input.required<FieldTree<SelectedCable>>();
  readonly systemVoltage = input.required<FieldTree<SystemVoltage>>();

  private readonly mceApiService = inject(MceApiService);

  isArmouredRx = rxResource({
    params: () => ({ libraryGuid: this.selectedCable().libraryGuid().value() }),
    stream: ({ params }) =>
      params.libraryGuid === ''
        ? of({ value: false })
        : this.mceApiService.v1CableLibraryArmoured(params.libraryGuid)
  });
  isArmoured = computed(() => this.isArmouredRx.value()?.value ?? false);

  isScreenedRx = rxResource({
    params: () => ({ libraryGuid: this.selectedCable().libraryGuid().value() }),
    stream: ({ params }) =>
      params.libraryGuid === ''
        ? of({ value: false })
        : this.mceApiService.v1CableLibraryScreened(params.libraryGuid)
  });
  isScreened = computed(() => this.isScreenedRx.value()?.value ?? false);


  hasSpareCoreRx = rxResource({
    params: () => ({
      libraryGuid: this.selectedCable().libraryGuid().value(),
      systemVoltage: createSystemVoltageDto(this.systemVoltage())()
    }),
    stream: ({ params }) =>
      params.libraryGuid === '' || !this.systemVoltage()().valid()
        ? of({ value: false })
        : this.mceApiService.v1CableLibrarySparecore(params.libraryGuid,params.systemVoltage)
  });
  hasSpareCore = computed(() => this.hasSpareCoreRx.value()?.value ?? false);


}
